import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { day } from 'libs/day';
import { FlexTimeActivityInstance, MingaPermission } from 'libs/domain';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, firstValueFrom, from, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  MODAL_OVERLAY_DATA,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import { SystemAlertSnackBarData } from '@shared/components/system-alert';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { FlexTimeActivityInstanceService, FlexTimePeriodService } from '@shared/services/flex-time';
import { FlexTimePermissionsService } from '@shared/services/flex-time/flex-time-permissions';

import {
  ActivityCardFields,
  ActivitySelectorModalData,
  ActivitySelectorModalResponse,
  FtmSharedMessages,
} from '../../ftm-shared.constants';
import { PermissionsService } from '@app/src/app/permissions';

@Component({
  selector: 'mg-ftm-activity-selector-modal',
  templateUrl: './ftm-activity-selector-modal.component.html',
  styleUrls: ['./ftm-activity-selector-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FtmActivitySelectorModalComponent implements OnInit, OnDestroy {
  public MESSAGES = FtmSharedMessages;
  public CARD_FIELDS = [
    ActivityCardFields.NAME,
    ActivityCardFields.DESCRIPTION,
    ActivityCardFields.TEACHER,
    ActivityCardFields.SEATS,
  ];

  private readonly _destroyed = new ReplaySubject<void>(1);

  private _formState = new BehaviorSubject<'idle' | 'loading'>('loading');
  public formState$ = this._formState.asObservable();

  public readonly form: FormGroup<{
    flexTimeActivity: FormControl<FlexTimeActivityInstance>;
    date: FormControl<day.Dayjs | null>;
    periodFilter: FormControl<number>;
    searchFilter: FormControl<string>;
    teacherFilter: FormControl<string>;
  }> = this._fb.group({
    flexTimeActivity: [null, Validators.required],
    date: [this.data.startDate || day()],
    periodFilter: [this.data.periodId],
    searchFilter: [''],
    teacherFilter: [this.data.teacher?.hash || null],
  });

  private _flexPeriods$ = this.form.controls.date.valueChanges.pipe(
    startWith(this.form.controls.date.value),
    takeUntil(this._destroyed),
    tap(() => this._formState.next('loading')),
    switchMap(date => {
      const fetcher = this.data.periodId

        // if we have a pre defined periodId. eg student registration modal we only want to fetch actvitiies for it
        // otherwise we rely on the date to fetch the upcoming periods
        ? async () => {
            const period = await this._flexPeriods.fetch(this.data.periodId);
            return [period];
          }
        : async () => this._flexPeriods.fetchAllMinimal(date, date);

      return from(fetcher()).pipe(
        tap(periods => {
          const upcomimgPeriod = this._flexPeriods.getUpcomingPeriod(day(), periods);
          // lets only set initial period if we dont have a preset period
          if (upcomimgPeriod && !this.data.periodId) {
            this.form.controls.periodFilter.setValue(upcomimgPeriod.id);
          }
        }),
        catchError(error => {
          this._systemAlertSnackBar.open({
            type: 'error',
            message: FtmSharedMessages.MODAL_ERROR_FETCHING_ACTIVITIES,
          });
          return of([]);
        }),
      );
    }),
    tap(() => this._formState.next('idle')),
    shareReplay(1),
  );

  public periodOptions$ = this._flexPeriods$.pipe(
    map(periods => {
      return periods.map(p => ({
        value: p.id,
        label: p.title,
      }));
    }),
    startWith([]),
  );

  public activePeriodIsPastRegistrationDeadline$: Observable<boolean> = combineLatest([
    this._flexPeriods$,
    this.form.controls.periodFilter.valueChanges.pipe(startWith(this.data?.periodId || null)),
  ]).pipe(
    map(([periods, periodId]) => {
      const isFlexAdmin = this._flexPermissionService.isFlexTimeAdmin();

      // flex admins can do everything
      if (isFlexAdmin) return false;

      const isStudent = this._permissions.hasPermission(MingaPermission.STUDENT_TOOLS);

      if (isStudent) {
        const studentDeadline = periods.find(p => p.id === periodId)?.studentRegistrationDeadline;
        if (!studentDeadline) return false;

        return studentDeadline.isSameOrBefore(day(), 'minute');
      }

      else {
        const teacherDeadline = periods.find(p => p.id === periodId)?.teacherRegistrationDeadline;
        if (!teacherDeadline) return false;

        return teacherDeadline.isSameOrBefore(day(), 'minute');
      }
    }),
    startWith(false),
  );

  private _isFlexTimeRegistrant = this._flexPermissionService.isFlexTimeRegistrant();

  public MIN_DATE = day();
  public TODAY = day();

  private _loadingActivitiesSubject = new BehaviorSubject<boolean>(false);
  public loadingActivities$ = this._loadingActivitiesSubject.asObservable();

  private _activityInstances$: Observable<FlexTimeActivityInstance[]> =
    this.form.controls.periodFilter.valueChanges.pipe(
      startWith(this.form.controls.periodFilter.value),
      tap(() => this._loadingActivitiesSubject.next(true)),
      switchMap(periodId => {
        if (!periodId) return of([]);

        return from(
          this._ftActivityInstances.fetchAll({
            periodId,
          }),
        );
      }),
      tap(() => this._loadingActivitiesSubject.next(false)),
      catchError(error => {
        this._systemAlertSnackBar.open({
          type: 'error',
          message: FtmSharedMessages.MODAL_ERROR_FETCHING_ACTIVITIES,
        });
        return of([]);
      }),
      startWith([]),
      shareReplay(1),
    );

  public filteredActivityInstances$ = combineLatest([
    this._activityInstances$,
    this.form.valueChanges.pipe(startWith(this.form.value), debounceTime(100)),
  ]).pipe(
    map(([activityInstances, form]) => {
      const { periodFilter, searchFilter, teacherFilter } = form;
      const filterFunctions = [];

      // filtering functions

      // if we're locked to a specific teacher, we need to filter if teachers have permission to manage that period
      const filterByPeriod = this._teacherInput
        ? (a => a.flexTimePeriod.enableStudentManagement && a.flexTimePeriodId === periodFilter)
        : (a => a.flexTimePeriodId === periodFilter);

      const filterByCurrentActivityId = a => a.id !== this.data.currentActivityId;
      const filterByTeacher = a => {
        const isMainTeacher = a.flexTimeActivity?.createdByPerson.hash === teacherFilter;
        const isAdditonalTeacher = a.flexTimeActivity?.teacherList?.members?.some(
          member => member.hash === teacherFilter,
        );
        return isMainTeacher || isAdditonalTeacher;
      };
      const filterBySearch = a => a.flexTimeActivity?.name?.toLowerCase().includes(searchFilter.trim().toLowerCase());
      const filterByAllowStudentsToRegister = a => a.flexTimeActivity?.allowStudentToRegister;

      if (periodFilter) filterFunctions.push(filterByPeriod);
      if (this.data?.currentActivityId) filterFunctions.push(filterByCurrentActivityId);
      if (teacherFilter) filterFunctions.push(filterByTeacher);
      if (searchFilter) filterFunctions.push(filterBySearch);
      if (this._isFlexTimeRegistrant) filterFunctions.push(filterByAllowStudentsToRegister);

      // using reduce so we only iterate over the items once just in case this
      // list gets big
      return activityInstances.reduce((result, obj) => {
        if (filterFunctions.every(filterFn => filterFn(obj))) {
          result.push(obj);
        }
        return result;
      }, []);
    }),
  );

  public teacherOptions$ = this._activityInstances$.pipe(
    map(activities => {
      const teacherOptions: Array<{
        value: string;
        label: string;
      }> = (activities || [])
        .filter(a => a.flexTimeActivity?.createdByPerson)
        .map(a => ({
          value: a.flexTimeActivity.createdByPerson.hash,
          label: a.flexTimeActivity.createdByPerson.name,
        }));

      const deduped = Array.from(new Map(teacherOptions.map(item => [item.value, item])).values());

      return deduped;
    }),
    startWith([]),
  );

  // use view/model pattern to manage reactive template values
  public vm$ = combineLatest([
    this.formState$,
    this.activePeriodIsPastRegistrationDeadline$,
    this.periodOptions$,
    this.filteredActivityInstances$,
    this.teacherOptions$,
    this.loadingActivities$,
  ]).pipe(
    map(
      ([
        formState,
        activePeriodIsPastRegistrationDeadline,
        periodOptions,
        filteredActivityInstances,
        teacherOptions,
        loadingActivities,
      ]) => ({
        formState,
        activePeriodIsPastRegistrationDeadline,
        periodOptions,
        filteredActivityInstances,
        teacherOptions,
        loadingActivities,
      }),
    ),
  );

  // this is used to check if the teacher input is set, because it changes some of the filtering logic
  private _teacherInput = this.data.teacher?.hash || null;

  constructor(
    @Inject(MODAL_OVERLAY_DATA) public data: ActivitySelectorModalData,
    private _modalRef: ModalOverlayRef<ActivitySelectorModalResponse, ActivitySelectorModalData>,
    private _flexPeriods: FlexTimePeriodService,
    private _fb: UntypedFormBuilder,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _flexPermissionService: FlexTimePermissionsService,
    private _ftActivityInstances: FlexTimeActivityInstanceService,
    private _permissions: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.form.controls.date.valueChanges.pipe(takeUntil(this._destroyed)).subscribe(date => {
      this._onDateChange(date);
    });

    this.form.controls.periodFilter.valueChanges.pipe(takeUntil(this._destroyed)).subscribe(date => {
      this.form.controls.flexTimeActivity.setValue(null, { emitEvent: false });
    });
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  public setActive(activity) {
    const activityControl = this.form.controls.flexTimeActivity;
    activityControl.setValue(activity);
    activityControl.markAsDirty();
  }

  public async cancel(): Promise<void> {
    this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
  }

  public async submit() {
    const isStaff = this._flexPermissionService.isFlexTimeSelfManager();
    const canManageActivityRegistration = this._flexPermissionService.canManageActivityRegistration(
      this.form.controls.flexTimeActivity.value,
    );

    if (isStaff && !canManageActivityRegistration) {
      const flexPeriods = await firstValueFrom(this._flexPeriods$);
      const flexTimePeriod = flexPeriods.find(p => p.id === this.form.controls.periodFilter.value);
      if (!flexTimePeriod?.enableStudentManagement) {
        const config: SystemAlertSnackBarData = {
          type: 'error',
          message: 'Activity is locked',
        };
        this._systemAlertSnackBar.open(config);
        return;
      }
    }

    this._modalRef.close(ModalOverlayServiceCloseEventType.CREATE, {
      activityInstance: this.form.controls.flexTimeActivity.value,
    });
  }

  private _onDateChange(date: day.Dayjs) {
    this.form.controls.searchFilter.setValue('', { emitEvent: false });
    if(!this._teacherInput) this.form.controls.teacherFilter.setValue(null, { emitEvent: false });
    this.form.controls.periodFilter.setValue(null, { emitEvent: false });
  }
}
